<script setup lang="ts">
  import { Primitive, type PrimitiveProps } from 'radix-vue';
  import type { HTMLAttributes } from 'vue';

  import { cn } from '@/lib/utils';

  import { type ButtonVariants, buttonVariants } from '.';

  enum IconType {
    delete = 'fa-solid fa-trash',
    remove = 'fa-solid fa-trash',
    download = 'fa-solid fa-download',
    refresh = 'fa-solid fa-arrows-rotate',
    upload = 'fa-solid fa-upload',
    edit = 'fa-solid fa-pencil',
    add = 'fa-solid fa-plus',
    save = 'fa-solid fa-save',

    home = 'fa-solid fa-home',
    settings = 'fa-solid fa-gear',
    back = 'fa-solid fa-arrow-left',
    ellipsis = 'fa-solid fa-ellipsis-vertical',

    success = 'fa-solid fa-check',
    error = 'fa-solid fa-xmark',
    warning = 'fa-solid fa-triangle-exclamation',

    user = 'fa-solid fa-user',
    search = 'fa-solid fa-magnifying-glass',

    show = 'fa-solid fa-eye',
    hide = 'fa-solid fa-eye-slash',
  }

  interface Props extends PrimitiveProps {
    isLoading?: boolean;
    icon?: IconType | string;
    size?: ButtonVariants['size'];
    class?: HTMLAttributes['class'];
    variant?: ButtonVariants['variant'];
  }

  const props = withDefaults(defineProps<Props>(), {
    as: 'button',
    icon: undefined,
    isLoading: false,
  });

  const getIconClass = (icon: IconType | string) => {
    if (typeof icon === 'string' && Object.keys(IconType).includes(icon)) {
      return IconType[icon as keyof typeof IconType];
    }

    return icon;
  };
</script>

<template>
  <Primitive
    :as="as"
    :as-child="asChild"
    :class="
      cn(
        buttonVariants({ variant, size }),
        props.class,
        'flex items-center gap-2',
      )
    "
  >
    <div v-if="isLoading">
      <slot name="loading">
        <IsLoading class="h-4 w-4" />
      </slot>
    </div>
    <template v-else>
      <i
        v-if="icon && !isLoading"
        :class="[getIconClass(icon), 'inline-block', isLoading ? 'hidden' : '']"
      ></i>
    </template>

    <slot />
  </Primitive>
</template>
